import Vue from 'vue';
import Konva from 'konva';
import axios from 'axios';
import './_wavetransformers.js';
import VueAxios from 'vue-axios';
import defaults from './_defaults.js';
import WaveSurfer from 'wavesurfer.js';
import QRCode from "@keeex/qrcodejs-kx";
import makeTextTransformer from './_texttransformer.js';
import { debounce, getConvertedString } from './_helpers.js';
import { drawBars, drawModern, drawWave } from './wavesurfer/drawer.ayv.js';

import store from '~/scripts/store/index.js';
Konva.pixelRatio = 1;
Konva.showWarnings = false;

Vue.use(VueAxios, axios);
class Pipeline {

    constructor() {
        if(Pipeline._instance) {
            return Pipeline._instance;
        }

        Pipeline._instance = this;

        this.setupPipeline();

        this.setupWatchers();

        // run pipeline on initialization
        this.runPipeline();
    }

    /**
     * MAIN METHOD for running the image generation.
     * This runs the pipeline to generate the soundwave image.
     */
    setupPipeline() {
        this.runTextPipeline = debounce(async () => {
            this.addTextLayer();

            await this.addTextsToCanvas();

            this.enableTextTransformers(store.state.step == 3);
        }, 20);

        this.runPipeline = debounce(async (soundFilesToWaveForm = false) => {
            await this.createStage();

            this.setStageBaseBackgrounds();

            if (soundFilesToWaveForm) {
                await this.soundFilesToWaveForm();
            }

            setTimeout(() => {
                this.setBackgroundOrder();
            }, 100);

            this.runTextPipeline();

            if (store.state.stepsDisabled.indexOf(5) === -1) {
                this.runQRCodePipeline();
            }

            this.generateTopLayer();
        }, 60);
    }

    /**
     * Setup which configuration values should be watched and how to react when they get changed.
     */
    setupWatchers() {
        store.watch((state) => state.stageConfiguration.format, () => {
            this.runPipeline(true);

            store.commit('updateField', {
                path: 'selectedFrameSize',
                value: null
            });
        });

        store.watch((state) => state.stageConfiguration.orientation, () => {
            this.runPipeline(true);
        });

        // when soundFiles change, make sure there is a waveConfiguration for every soundFile
        store.watch((state) => state.soundFiles, (newSoundFiles) => {
            // clear not anymore needed configurations
            if (store.state.removedIndex !== null) {
                store.state.waveConfigurations.splice(store.state.removedIndex, 1);
            }

            // check soundFiles. if configuration does not exist create from default
            newSoundFiles.forEach((file, idx) => {
                if(!store.state.waveConfigurations[idx] || store.state.insertIndex === idx) {
                    let defaultConfiguration = JSON.parse(JSON.stringify(defaults.waveConfiguration));
                    // overwrite with customizations
                    let waveConfiguration = {...defaultConfiguration, ...store.state.globalPresets};

                    // custom options for theme love
                    if(store.state.theme.title.toLowerCase() === 'love') {
                        waveConfiguration = {...waveConfiguration, ...defaults.loveWaveConfigurations[idx]};
                        if (store.state.removedConfig) {
                            waveConfiguration.style = store.state.removedConfig.style;
                            waveConfiguration.color = store.state.removedConfig.color;
                            waveConfiguration.backgroundColor = store.state.removedConfig.backgroundColor;

                            if (store.state.removedConfig.barGap) {
                                waveConfiguration.barGap = store.state.removedConfig.barGap;
                            }

                            if (store.state.removedConfig.barWidth) {
                                waveConfiguration.barWidth = store.state.removedConfig.barWidth;
                            }
                        }

                        // switch colors for second wave
                        if (idx === 1 && !store.state.removedConfig) {
                            let backgroundColor = waveConfiguration.color;
                            let color = waveConfiguration.backgroundColor;

                            waveConfiguration.color = color;
                            waveConfiguration.backgroundColor = backgroundColor;
                        }
                    } else {
                        if (store.state.removedConfig) {
                            waveConfiguration.style = store.state.removedConfig.style;
                            waveConfiguration.color = store.state.removedConfig.color;
                            waveConfiguration.backgroundColor = store.state.removedConfig.backgroundColor;

                            if (store.state.removedConfig.barGap) {
                                waveConfiguration.barGap = store.state.removedConfig.barGap;
                            }

                            if (store.state.removedConfig.barWidth) {
                                waveConfiguration.barWidth = store.state.removedConfig.barWidth;
                            }
                        }
                    }

                    waveConfiguration.idx = idx;

                    if (store.state.insertIndex !== null) {
                        store.state.waveConfigurations.splice(idx, 0, waveConfiguration);
                    } else {
                        store.state.waveConfigurations[idx] = waveConfiguration;
                    }

                    store.state.removedConfig = null;
                    store.state.insertIndex = null;
                }
            });

            store.commit('updateField', {
                path: 'soundFilesChanged',
                value: true
            });
        });
    }

    /**
     * Create the empty canvas
     * - dimension (4:3, 3:2 / orientation)
     * - background (color/image)
     * - border (yes or no)
     */
    createStage() {
        return (new Promise(async (resolve) => {
            let stage = store.state.stage;
            let size = this._getCanvasSize();

            if (stage === null) {
                store.commit('updateField', {
                    path: 'stage',
                    value: new Konva.Stage({
                        container: 'ayv-stage',   // id of container <div>
                        width: size.width,
                        height: size.height,
                        draggable: false
                    })
                });
            } else {
                stage.width(size.width);
                stage.height(size.height);
            }

            let imageAttributes = store.state.waveConfigurations[0] ?
                this.getWaveImageAttributes(0, store.state.waveConfigurations[0]) :
                {
                    width: size.width,
                    height: size.height
                };

            store.commit('updateField', {
                path: `waveformContainerStyle`,
                value: `width: ${imageAttributes.width}px; height: ${imageAttributes.height}px`
            });

            this.setResponsiveStageWidth();
            resolve();
        }));
    }

    /**
     * Return the size of the stage according to definitions in configurator
     * @return {{width: number, height: number}}
     * @private
     */
    _getCanvasSize() {
        let result;

        switch(store.state.stageConfiguration.format) {
            case "4-3":
                result = {
                    width: 1200,
                    height: 900
                };
                break;
            case "3-2":
                result = {
                    width: 1200,
                    height: 800
                };
                break;
            case "panorama":
                result = {
                    width: 1200,
                    height: 400
                };
                break;
        }

        if(store.state.stageConfiguration.orientation === 'portrait') {
            result = {
                width: result.height,
                height: result.width,
            };
        }

        return result;
    }

    setResponsiveStageWidth() {
        const size = this._getCanvasSize();

        Vue.nextTick(() => {
            let step = store.state.step;
            let stage = store.state.stage;
            let controlsTop = document.querySelector('.controls-top');
            let container = document.querySelector('.preview-wrapper');
            let controlsBottom = document.querySelector('.controls-bottom');
            let wavePreviewContainer = document.querySelector('.wave-preview-container');

            let paddingY = 30;
            // let paddingX = step > 1 ? 75 : 150;
            let paddingX = 30;

            if (stage && wavePreviewContainer && (step >= 1 && step <= 4)) {
                // fit stage into parent
                let containerWidth = container.offsetWidth;
                let containerHeight = container.offsetHeight;
                containerHeight = controlsTop ? containerHeight - controlsTop.offsetHeight : containerHeight;
                containerHeight = controlsBottom ? containerHeight - controlsBottom.offsetHeight : containerHeight;
                const stageRatio = stage.width() / stage.height();
                const containerRatio = containerWidth / containerHeight;
                let scale = containerRatio >= stageRatio ?
                    (containerHeight - (paddingY * 2)) / size.height :
                    (containerWidth - (paddingX * 2)) / size.width;

                if (scale > 1) {
                    scale = 1;
                }

                store.commit('updateField', {
                    path: 'stageScale',
                    value: scale
                });

                store.commit('updateField', {
                    path: 'stageScaleChanged',
                    value: true
                });
            }
        });
    }

    setStageBaseBackgrounds() {
        let stage = store.state.stage;
        let bg = store.state.stageConfiguration.background;

        let BGLayer = stage.findOne('#stage_background');
        if (BGLayer) {
            BGLayer.destroyChildren().batchDraw();
        } else {
            BGLayer = new Konva.Layer({id: 'stage_background'});
            BGLayer.canvas._canvas.style.zIndex = 0;
            stage.add(BGLayer);
        }

        let waveLayer = stage.findOne('#waves_layer');
        if (waveLayer) {
            waveLayer.destroyChildren().batchDraw();
        } else {
            waveLayer = new Konva.Layer({id: 'waves_layer'});
            stage.add(waveLayer);
        }

        // set base background of Stage
        let background = new Konva.Rect({
            x: 0,
            y: 0,
            id: 'base_background',
            width: stage.width(),
            height: stage.height(),
            fill: bg.color,
            zIndex: 0,
            // remove background from hit graph for better perf
            // because we don't need any events on the background
            listening: false,
        });

        BGLayer.add(background);
        BGLayer.batchDraw();

        this.setIndividualBackgrounds();
        this.updateStageBorder();
    }

    async setIndividualBackgrounds() {
        await this.setImageBackground();
        await this.setTextBackground();
        this.setBackgroundOrder();
    }

    setImageBackground() {
        return (new Promise((resolve) => {
            let stage = store.state.stage;
            let bg = store.state.stageConfiguration.background;
            let imageBackground = stage.findOne('#background_image');
            let stageBackground = stage.findOne('#stage_background');

            if (imageBackground) {
                imageBackground.destroy();
                stageBackground.batchDraw();
            }

            if(bg.type == 'image' && bg.image) {
                let transform = store.state.stageConfiguration.background.transform;
                let that = this;

                if(bg.image.substr(0, 5) === 'data:') {
                    var image = new Image();

                    image.onload = async function() {
                        let imageProps = that.getBackgroundImageAttributes(image.width, image.height, stage, transform);
                        let backgroundImg = new Konva.Image(imageProps);
                        backgroundImg.image(image);

                        // add image to the layer
                        stageBackground.add(backgroundImg);
                        stageBackground.batchDraw();
                        await that.setWaveformBackgroundGradient();
                        resolve();
                    }

                    // set image source and load image
                    image.src = bg.image;
                } else {
                    // load image from url
                    Konva.Image.fromURL(bg.image, async (image) => {
                        let imageProps = that.getBackgroundImageAttributes(image.width(), image.height(), stage, transform);

                        image.setAttrs(imageProps);
                        stageBackground.add(image);
                        stageBackground.batchDraw();
                        await that.setWaveformBackgroundGradient();
                        resolve();
                    });
                }
            } else {
                for (let idx = 0; idx < store.state.waveConfigurations.length; idx++) {
                    const backgroundOverlay = stage.findOne(`#background_overlay_${idx}`);
                    if (backgroundOverlay) {
                        backgroundOverlay.destroy();
                    }
                }
                resolve();
            }
        }));
    }

    async setWaveformBackgroundGradient() {
        return (new Promise(async (resolve) => {
            let state = store.state;
            let stage = state.stage;
            let stageBackground = stage.findOne('#stage_background');

            for (let idx = 0; idx < state.waveConfigurations.length; idx++) {
                const minHeight = 180;
                let layerID = `background_overlay_${idx}`;
                let existingGradient = stage.findOne(`#${layerID}`);
                const waveConfiguration = state.waveConfigurations[idx];
                let height = stage.height() * .75 * waveConfiguration.barHeight;
                height = height < minHeight ? minHeight : height;
                const overlayY = (stage.height() / 2) - (height / 2);
                const y = waveConfiguration.y ? overlayY + waveConfiguration.y - defaults.stageBorderWidth : overlayY;

                if (existingGradient) {
                    existingGradient.destroy();
                    stageBackground.batchDraw();
                }

                const backgroundGradient = new Konva.Rect({
                    id: layerID,
                    x: 0,
                    y: y,
                    width: stage.width(),
                    height: height,
                    fillLinearGradientStartPoint: { x: 0, y: 0 },
                    fillLinearGradientEndPoint: { x: 0, y: height },
                    fillLinearGradientColorStops: [0, 'rgba(0,0,0,0)', 0.5, 'rgba(0,0,0,.55)', 1, 'rgba(0,0,0,0)']
                });

                stageBackground.add(backgroundGradient);
                backgroundGradient.moveToTop();
            }

            resolve();
        }));
    }

    setBackgroundEffect() {
        let stage = store.state.stage;
        let bgImage = stage.findOne('#background_image');
        let backgroundEffect = store.state.stageConfiguration.background.effect;
        let stageBackground = stage.findOne('#stage_background');

        bgImage.clearCache();

        if (backgroundEffect == 'grayscale') {
            bgImage.cache();
            bgImage.filters([Konva.Filters.Grayscale]);
        } else if (backgroundEffect == 'sepia') {
            bgImage.cache();
            bgImage.filters([Konva.Filters.Sepia]);
        } else {
            bgImage.filters([]);
        }

        stageBackground.batchDraw();
    }

    setTextBackground() {
        return (new Promise((resolve) => {
            let stage = store.state.stage;
            let bg = store.state.stageConfiguration.background;
            let textBackground = stage.findOne('#text_background');
            let stageBackground = stage.findOne('#stage_background');

            if (textBackground) {
                textBackground.destroy();
                stageBackground.batchDraw();
            }

            if (bg.type == 'text' && (bg.text || bg.textarea)) {
                var text = new Konva.Text({
                    x: bg.text_x,
                    y: bg.text_y,
                    align: "center",
                    fill: bg.text_fill,
                    id: "text_background",
                    fontSize: bg.text_size,
                    scaleX: bg.text_scaleX,
                    scaleY: bg.text_scaleY,
                    verticalAlign: "middle",
                    fontFamily: bg.text_fontFamily,
                    opacity: (100 - bg.text_transparency) / 100,
                    text: bg.text_style === 'fill' ? bg.textarea : bg.text,
                });

                if (bg.text_style === 'fill') {
                    // let y = 0;
                    // switch (bg.text_position) {
                    //     case 'middle':
                    //         y = (stage.height() / 2) - (text.height() / 2);
                    //         break;
                    //     case 'bottom':
                    //         y = stage.height() - text.height();
                    //         break;

                    //     default:
                    //         y = 0;
                    //         break;
                    // }

                    // text.x((stage.width() / 2) - (text.width() / 2));
                    // text.y(y);
                    stageBackground.add(text);
                    this.enableBackgroundTextTransformers();
                    stageBackground.batchDraw();

                    resolve();
                }

                if (bg.text_style === 'repeat') {
                    var text = text.toDataURL();
                    Konva.Image.fromURL(text, (backgroundImg) => {
                        var background = new Konva.Rect({
                            x: 0,
                            y: 0,
                            // zIndex: 3,
                            width: stage.width(),
                            id: "text_background",
                            height: stage.height(),
                            fillPatternImage: backgroundImg.image(),
                            // remove background from hit graph for better perf
                            // because we don't need any events on the background
                            listening: false,
                        });
                        stageBackground.add(background);
                        stageBackground.batchDraw();
                        resolve();
                    });
                }
            } else {
                resolve();
            }
        }));
    }

    addBackgroundCropControls() {
        const stage = store.state.stage;
        const hasTouch = 'ontouchstart' in window;
        let stageBackground = stage.findOne('#stage_background');
        let bgImage = stage.findOne('#background_image');

        let transformer = store.state.stageConfiguration.background.imageTransformer = new Konva.Transformer({
            ...defaults.transformerConfiguration,
            anchorSize: hasTouch ? 70 : 30,
            anchorCornerRadius: hasTouch ? 70 : 30,
            nodes: [bgImage],
            keepRatio: true,
            rotationSnaps: [0, 90, 180, 270],
            enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
            boundBoxFunc: (oldBox, newBox) => {
                if (newBox.width < 10 || newBox.height < 10) {
                    return oldBox;
                }

                store.state.stageConfiguration.background.transform = newBox;
                return newBox;
            },
        });

        this.wavesDraggable(false);
        bgImage.draggable(true);
        stageBackground.add(transformer);
        stageBackground.batchDraw();
    }

    removeBackgroundCropControls() {
        const stage = store.state.stage;
        if (stage) {
            let stageBackground = stage.findOne('#stage_background');
            let bgImage = stage.findOne('#background_image');
            this.wavesDraggable();

            if (stageBackground && bgImage) {
                let transformer = store.state.stageConfiguration.background.imageTransformer;

                transformer.destroy();
                bgImage.draggable(false);
                stageBackground.batchDraw();
                this.setBackgroundOrder();
            }
        }
    }

    initializeWaveTransformers() {
        this.wavesDraggable(false);

        const waves = store.state.waveConfigurations;
        if (waves.length > 0) {
            let stage = store.state.stage;
            let wavesLayer = stage.findOne('#waves_layer');

            for (let idx = 0; idx < waves.length; idx++) {
                let wave = stage.findOne(`#wave_image_${idx}`);
                let config = store.state.waveConfigurations[idx];
                let imageUrl = store.state.wavesurferWaves[idx].exportImage('image/png', 1, 'dataURL');
                let cropImageAttributes = {...wave.getAttrs()};

                if (config.cropOptions) {
                    let cropX = config.cropOptions.xPercent * cropImageAttributes.width;
                    let cropWidth = config.cropOptions.widthPercent * cropImageAttributes.width;

                    cropImageAttributes.cropTransform = {
                        height: cropImageAttributes.height,
                        rotation: 0,
                        scaleX: 1,
                        scaleY: 1,
                        width: cropImageAttributes.width,
                        x: cropX,
                        y: 0
                    };
                    cropImageAttributes.width = cropWidth;
                    cropImageAttributes.x = cropX * -1 + cropImageAttributes.x;
                }

                Konva.Image.fromURL(imageUrl, function(img) {
                    img.setAttrs({
                        ...cropImageAttributes,
                        opacity: 1,
                        id: `wave_crop_image_${idx}`
                    });

                    wavesLayer.add(img);
                    wavesLayer.batchDraw();
                    img.offsetX(0);
                    img.cropStart(store.state.waveConfigurations[idx]);
                });

                wave.opacity(0);
            }
        }
    }

    removeWaveTransformers() {
        const waves = store.state.waveConfigurations;

        if (waves.length > 0) {
            for (let idx = 0; idx < waves.length; idx++) {
                this.removeSingleWaveTransformer(idx);
            }
        }
    }

    removeSingleWaveTransformer(idx) {
        if (store.state.stage) {
            let wave = store.state.stage.findOne(`#wave_image_${idx}`);
            if (wave) {
                let imageAttributes = this.getWaveImageAttributes(idx, store.state.waveConfigurations[idx]);

                wave.offsetX(imageAttributes.offsetX);
                wave.opacity(imageAttributes.opacity);

                let cropImage = store.state.stage.findOne(`#wave_crop_image_${idx}`);
                if (cropImage) {
                    cropImage.cropEnd();
                    cropImage.destroy();
                }
                wave.getLayer().batchDraw();

                this.singleSoundFileToWaveForm(store.state.soundFiles[idx], idx);
            }
        }
    }

    setBackgroundOrder() {
        let stage = store.state.stage;
        let stageBackground = stage.findOne('#stage_background');
        let backgroundImage = stage.findOne('#background_image');
        let textBackground = stage.findOne('#text_background');
        let stageBorder = stage.findOne('#stage_border');
        let stageBorder2 = stage.find('.stage_border2');

        if (backgroundImage) {
            backgroundImage.moveToTop();
        }

        for (let idx = 0; idx < store.state.waveConfigurations.length; idx++) {
            const backgroundOverlay = stage.findOne(`#background_overlay_${idx}`);
            if (backgroundOverlay) {
                backgroundOverlay.moveToTop();
            }
        }

        if (textBackground) {
            textBackground.moveToTop();
        }

        if (stageBorder) {
            stageBorder.moveToTop();
        }

        if (stageBorder2) {
            stageBorder2.moveToTop();
        }

        stageBackground.batchDraw();
    }

    generateTopLayer() {
        let stage = store.state.stage;

        let topLayer = stage.findOne('#top_layer');
        if (!topLayer) {
            topLayer = new Konva.Layer({
                id: 'top_layer',
                zIndex: 1500,
                width: stage.width(),
                height: stage.height()
            });
            stage.add(topLayer);
        }

        topLayer.canvas._canvas.style.zIndex = 10000;
        topLayer.moveToTop();
        topLayer.batchDraw();
    }

    /**
     * Generates WaveSurfer.js canvas for all the sound files.
     */
    soundFilesToWaveForm(readNewSoundFile = false) {
        var promises = [];
        let stage = store.state.stage;
        if (stage) {
            let wavesLayer = stage.findOne('#waves_layer');

            // destroy all waves on stage first
            wavesLayer.destroyChildren().batchDraw();
            const borderWidth = (store.state.stageConfiguration.border === 'border' &&
                store.state.stageConfiguration.background.type !== 'image') ?
                defaults.stageBorderWidthVisible :
                defaults.stageBorderWidth;
            wavesLayer.clip({
                x: borderWidth,
                y: borderWidth,
                width: stage.width() - (borderWidth * 2),
                height: stage.height() - (borderWidth * 2)
            });

            if (readNewSoundFile) {
                stage.find('.individual_background').destroy();
                stage.findOne('#stage_background').batchDraw();
            }

            store.state.soundFiles.forEach((soundFile, idx) => {
                promises.push(this.singleSoundFileToWaveForm(soundFile, idx, readNewSoundFile));
            });

            return Promise.all(promises);
        } else {
            return Promise.all(promises);
        }
    }

    /**
     * Given a soundfile and the configurated wave options, creates a WaveSurfer.js wave.
     * Results in the wave on a canvas.
     */
    singleSoundFileToWaveForm = (soundFile, idx, readNewSoundFile = false) => {
        return (new Promise(async (resolve) => {
            let wavesurfer = null;
            let existing = false;
            const stage = store.state.stage;
            let isUpload = soundFile.isUpload ?? false;
            let waveLayer = stage.findOne('#waves_layer');
            let config = store.state.waveConfigurations[idx];
            let love = store.state.theme.title.toLowerCase() === 'love';
            let imageAttributes = this.getWaveImageAttributes(idx, config);
            let waveFormOptions = this._getWaveFormOptions(idx, imageAttributes, love);

            if(store.state.wavesurferWaves[idx]) {
                existing = !readNewSoundFile;
                wavesurfer = store.state.wavesurferWaves[idx];

                store.commit('updateField', {
                    path: `waveformContainerStyle`,
                    value: `width: ${waveFormOptions.width}px; height: ${waveFormOptions.height}px`
                });

                for (const [param, value] of Object.entries(waveFormOptions)) {
                    if (param == 'height') {
                        wavesurfer.setHeight(waveFormOptions.height);
                    } else {
                        wavesurfer.params[param] = value;
                    }
                }
            } else {
                let waveConfig = {
                    container: `#waveform-${idx}`,
                    mediaControls: false,
                    hideScrollbar: true,
                    responsive: 10,
                    pixelRatio: 1,
                    // configurable
                    // barWidth: null,
                    // barRadius: 0,
                    // barGap: null,
                    // barMinHeight: 1,
                    // waveColor: 'violet',
                    ...waveFormOptions
                };

                store.commit('updateField', {
                    path: `waveformContainerStyle`,
                    value: `width: ${waveConfig.width}px; height: ${waveConfig.height}px`
                });

                // prepare the container
                wavesurfer = WaveSurfer.create(waveConfig);
            }

            config.initialX = imageAttributes.initialX;
            config.initialHeight = imageAttributes.height;
            config.initialWidth = imageAttributes.initialWidth;

            let waveGroup = new Konva.Group({
                ...imageAttributes,
                name: 'wave',
                draggable: true,
                id: `wave_image_${idx}`,
                y: config.y ?? imageAttributes.y,
                dragBoundFunc: function(pos) {
                    let y = pos.y;
                    const halfLayer = (imageAttributes.height / 2);
                    const minY = 0 - halfLayer;
                    const maxY = stage.height() - halfLayer;
                    const backgroundOverlay = stage.findOne(`#background_overlay_${idx}`);

                    if (pos.y >= maxY) {
                        y = maxY
                    }

                    if (pos.y <= minY) {
                        y = minY
                    }

                    if (backgroundOverlay) {
                        const overlayY = (halfLayer - backgroundOverlay.getHeight() / 2) + y
                        backgroundOverlay.setY(overlayY);
                        stage.findOne('#stage_background').batchDraw();
                    }

                    return {
                        x: this.absolutePosition().x,
                        y: y
                    }
                }
            }).on('dragstart', (event) => {
                this.showWaveDragGuides();
            }).on('dragend', (event) => {
                this.hideWaveDragGuides()
                config.y = event.target.y();
            });

            if (config.cropOptions) {
                waveGroup.clip({...this._getClipOptions(config)});
            }

            waveLayer.add(waveGroup);
            waveLayer.batchDraw();

            wavesurfer.drawer.drawWave = function (peaks, channelIndex, start, end) {
                const classic = store.state.waveConfigurations[idx].style == 'classic';
                return drawWave(idx, wavesurfer, peaks, channelIndex, start, end, classic);
            };

            wavesurfer.drawer.drawBars = function (peaks, channelIndex, start, end) {
                if (store.state.waveConfigurations[idx].style == 'modern') {
                    return drawModern(idx, wavesurfer, peaks, channelIndex, start, end);
                } else {
                    return drawBars(idx, wavesurfer, peaks, channelIndex, start, end);
                }
            };

            store.commit('updateField', {
                path: `wavesurferWaves.${idx}`,
                value: wavesurfer
            });

            if (!existing || readNewSoundFile) {
                // load the audio
                if(typeof soundFile === 'object') {
                    if(soundFile.url) {
                        wavesurfer.load(soundFile.url);
                    } else if(soundFile.blob instanceof Blob) {
                        wavesurfer.loadBlob(new Blob(soundFile.blob));
                    }
                } else {
                    wavesurfer.load(soundFile);
                }

                Vue.nextTick(() => {
                    wavesurfer.seekAndCenter(0.5);
                });
            } else {
                Vue.nextTick(() => {
                    wavesurfer.drawBuffer();
                });
            }

            await this.addSingleWaveFormToCanvas(store.state.wavesurferWaves[idx], idx, existing);
            resolve();
        }));
    };

    /**
     * Return the WaveSurfer.js options that were configured through the frontend controls.
     *
     * @param idx
     * @return {{barHeight: (Document.barHeight|number), waveColor: (*|string), barRadius: number, barMinHeight: (*|null)}}
     * @private
     */
    _getWaveFormOptions(idx, imageAttributes, love = false) {
        if(!idx) {
            idx = 0;
        }

        var w = store.state.waveConfigurations[idx] ?? {};

        var result = {
            barRadius: w.barRadius,
            barHeight: w.barHeight,
            waveColor: w.color || '#000',
            width: imageAttributes.width,
            height: imageAttributes.height,
            barMinHeight: w.barMinHeight || null,
        };


        // if barWidth, barGap and backgroundColor are set add them to the configuration
        // otherwise they will be the default (None). If we set them to NULL the result is different
        ['barWidth', 'barGap'].forEach((option) => {
            result[option] = w[option] && w[option] > 0 ? w[option] : null;
        });

        return result;
    }

    _getClipOptions(config) {
        return {
            y: 0,
            height: config.initialHeight,
            x: (config.cropOptions.xPercent * -1) * config.initialWidth,
            width: config.cropOptions.widthPercent * config.initialWidth,
        };
    }

    _getWaveOpacity(config) {
        return (100 - config.transparency) / 100;
    }

    /**
     * When a value is changed
     * @param waveIdx The sound wave (idx) that has changed
     * @param option The option that has changed
     * @param value The new value
     */
    setWaveValue(waveIdx, option, value) {
        if(value === null) {
            return;
        }

        store.commit('updateField', {
            path: `waveConfigurations.${waveIdx}.${option}`,
            value: value
        });
    }

    //---------------------------
    // add wave to canvas
    //---------------------------

    /**
     * Adds a single wave form to the canvas.
     * @param wave
     * @param idx
     * @return {Promise<void>}
     */
    async addSingleWaveFormToCanvas(wave, idx, existing = false) {
        return (new Promise((resolve) => {
            // wave.on method does not exist: nothing to do here
            if(!wave.on) {
                resolve();
                return;
            }

            if (existing) {
                // add image to layer
                this.updateWaveBackground(idx);
                resolve();
            }

            // add ready event to update wave after loading soundfile, but only if event does not exist
            if (wave.handlers === null) {
                wave.on('ready', () => {
                    // add image to layer
                    this.updateWaveBackground(idx);
                    resolve();
                });
            }
        }));
    }

    async updateWaveBackground(idx) {
        return (new Promise((resolve) => {
            let stage = store.state.stage;
            let config = store.state.waveConfigurations[idx];

            if (config) {
                // add background layer
                let imageAttributes = this.getWaveImageAttributes(idx, config, true);
                let baseBackground = stage.findOne('#base_background');
                let stageBackground = stage.findOne('#stage_background');
                let BGRect = stage.findOne(`#bg_rect_${idx}`);

                if (!BGRect) {
                    let background = new Konva.Rect({
                        listening: false,
                        id: `bg_rect_${idx}`,
                        x: imageAttributes.x,
                        y: imageAttributes.y,
                        fill: config.backgroundColor,
                        width: imageAttributes.width,
                        name: `individual_background`,
                        height: imageAttributes.height,
                        // remove background from hit graph for better perf
                        // because we don't need any events on the background
                    });

                    stageBackground.add(background);
                    background.moveToBottom();
                    baseBackground.moveToBottom();
                    stageBackground.batchDraw();
                } else {
                    BGRect.x(imageAttributes.x);
                    BGRect.y(imageAttributes.y);
                    BGRect.fill(config.backgroundColor);
                    BGRect.width(imageAttributes.width);
                    BGRect.height(imageAttributes.height);
                    BGRect.moveToBottom();
                    baseBackground.moveToBottom();
                    stageBackground.batchDraw();
                }
                resolve();
            } else {
                resolve();
            }
        }));
    };

    updateWaveFromUserConfig = debounce(async (wave, option, value) => {
        await this.generateTempStagePreview();

        if (option === 'waveColor') {
            wave.setWaveColor(value);
        } else {
            wave.params[option] = value;
        }

        wave.drawBuffer();
        await this.removeTempStagePreview();
    }, 300);

    updateWaveBackgroundColor = debounce(async (idx) => {
        await this.transitionBackground(idx);
        this.setBackgroundOrder();
    }, 300);

    async applyColorPreset(preset) {
        let color = preset.color;
        let backgroundColor = preset.background;

        await this.generateTempStagePreview();

        store.commit('updateField', {
            path: `stageConfiguration.color`,
            value: color
        });

        store.commit('updateField', {
            path: `stageConfiguration.borderColor`,
            value: color
        });

        if (store.state.theme.title.toLowerCase() === 'love') {
            store.commit('updateField', {
                path: `stageConfiguration.borderColor2`,
                value: backgroundColor
            });
        }

        await this.updateAllWaveColors(color, backgroundColor);
        this.updateStageBorder();
        this.updateTextColors(color);
        this.removeTempStagePreview();
    }

    async applySingleColorPreset(preset, idx) {
        let waves = store.state.wavesurferWaves;

        await this.generateTempStagePreview();

        let switchColors = idx == 1;
        let color = switchColors ? preset.background : preset.color;
        let backgroundColor = switchColors ? preset.color : preset.background;

        if (idx == 0) {
            store.commit('updateField', {
                path: `stageConfiguration.borderColor`,
                value: color
            });
        } else {
            store.commit('updateField', {
                path: `stageConfiguration.borderColor2`,
                value: color
            });
        }

        this.setWaveValue(idx, 'color', color);
        this.setWaveValue(idx, 'backgroundColor', backgroundColor);
        waves[idx].setWaveColor(color);

        setTimeout(async () => {
            await this.updateWaveBackground(idx);
        }, 50);

        this.updateStageBorder();
        this.updateTextColors(color);
        this.removeTempStagePreview();
    }

    async updateAllWaveColors(color, backgroundColor) {
        return (new Promise(async (resolve) => {
            let waves = store.state.wavesurferWaves;
            for (let idx = 0; idx < waves.length; idx++) {
                let switchColors = store.state.theme.title.toLowerCase() === 'love' && idx == 1;
                let newColor = switchColors ? backgroundColor : color;
                let newBackground = switchColors ? color : backgroundColor;

                this.setWaveValue(idx, 'color', newColor);
                this.setWaveValue(idx, 'backgroundColor', newBackground);
                waves[idx].setWaveColor(newColor);

                setTimeout(async () => {
                    await this.updateWaveBackground(idx);
                    if (idx == (waves.length - 1)) {
                        resolve();
                    }
                }, 50);
            }
        }));
    }

    async updateSingleWaveColor(idx, color, triggerChange) {
        return (new Promise(async (resolve) => {
            let state = store.state;
            let wave = state.wavesurferWaves[idx];

            if (triggerChange) {
                this.setWaveValue(idx, 'color', color);
                this.updateWaveFromUserConfig(wave, 'waveColor', color);

                if (idx == 0) {
                    store.commit('updateField', {
                        path: `stageConfiguration.color`,
                        value: color
                    });
                }

                if (state.stageConfiguration.border !== 'none') {
                    const path = state.theme.title.toLowerCase() === 'love' && idx == 1 ? 'borderColor2' : 'borderColor';
                    store.commit('updateField', {
                        path: `stageConfiguration.${path}`,
                        value: color
                    });
                    this.updateStageBorder();
                }

                this.updateTextColors(color);
            }
        }));
    }

    async transitionBackground(idx) {
        return (new Promise((resolve) => {
            let config = store.state.waveConfigurations[idx];
            const wave = store.state.stage.findOne(`#bg_rect_${idx}`);

            wave.to({
                duration: .35,
                easing: Konva.Easings.EaseInOut,
                fill: config.backgroundColor,
                onFinish: () => resolve()
            });
        }));
    }

    async updateStageBorder(addCropControls = false) {
        return (new Promise((resolve) => {
            if (store.state.stage) {
                let stageBackground = store.state.stage.findOne('#stage_background');
                let borderColor = store.state.stageConfiguration.borderColor;
                let borderColor2 = store.state.stageConfiguration.borderColor2;
                let stageBorder = stageBackground.findOne('#stage_border');
                const hasBorder = store.state.stageConfiguration.border === 'border' &&
                    store.state.stageConfiguration.background.type !== 'image';
                let borderPosition = hasBorder ?
                    defaults.stageBorderWidthVisible - (defaults.stageBorderWidthVisible / 2) :
                    defaults.stageBorderWidth - (defaults.stageBorderWidth / 2);

                if (borderColor2 && store.state.soundFiles.length !== 2) {
                    if (stageBorder) {
                        stageBorder.destroy();
                    }

                    const stageBorder2 = stageBackground.find('.stage_border2');
                    if (stageBorder2) {
                        stageBorder2.destroy();
                    }

                    stageBackground.batchDraw();
                    resolve();
                    return;
                }

                if (hasBorder) {
                    if (!stageBorder) {
                        let border = new Konva.Rect({
                            zIndex: 500,
                            strokeWidth: 3,
                            x: borderPosition,
                            y: borderPosition,
                            stroke: borderColor,
                            id: 'stage_border',
                            width: store.state.stage.width() - (borderPosition * 2),
                            height: store.state.stage.height() - (borderPosition * 2),

                            // remove background from hit graph for better perf
                            // because we don't need any events on the background
                            listening: false,
                        });

                        stageBackground.add(border);

                        stageBackground.batchDraw();
                    } else {
                        stageBorder.to({
                            duration: .35,
                            stroke: borderColor
                        });
                    }

                    if (borderColor2) {
                        let stageBorderTop = stageBackground.findOne('#stage_border2Top');
                        let stageBorderRight = stageBackground.findOne('#stage_border2Right');
                        let stageBorderBottom = stageBackground.findOne('#stage_border2Bottom');
                        const halfStage = store.state.stage.width() / 2;
                        const horizontalWidth = halfStage - borderPosition + 1;

                        if (!stageBorderTop) {
                            let stageBorderTop = new Konva.Rect({
                                zIndex: 510,
                                x: (store.state.stage.width() / 2),
                                y: borderPosition - 2,
                                fill: borderColor2,
                                id: 'stage_border2Top',
                                name: 'stage_border2',
                                width: horizontalWidth,
                                height: 3,

                                // remove background from hit graph for better perf
                                // because we don't need any events on the background
                                listening: false,
                            });
                            stageBackground.add(stageBorderTop);
                            stageBackground.batchDraw();
                        } else {
                            stageBorderTop.to({
                                duration: .35,
                                fill: borderColor2
                            });
                        }

                        if (!stageBorderRight) {
                            let stageBorderRight = new Konva.Rect({
                                zIndex: 510,
                                x: (store.state.stage.width() - borderPosition - 2),
                                y: borderPosition,
                                fill: borderColor2,
                                id: 'stage_border2Right',
                                name: 'stage_border2',
                                width: 3,
                                height: store.state.stage.height() - (borderPosition * 2),

                                // remove background from hit graph for better perf
                                // because we don't need any events on the background
                                listening: false,
                            });
                            stageBackground.add(stageBorderRight);
                            stageBackground.batchDraw();
                        } else {
                            stageBorderRight.to({
                                duration: .35,
                                fill: borderColor2
                            });
                        }

                        if (!stageBorderBottom) {
                            let stageBorderBottom = new Konva.Rect({
                                zIndex: 510,
                                x: (store.state.stage.width() / 2),
                                y: store.state.stage.height() - borderPosition - 2,
                                fill: borderColor2,
                                id: 'stage_border2Bottom',
                                name: 'stage_border2',
                                width: horizontalWidth,
                                height: 3,

                                // remove background from hit graph for better perf
                                // because we don't need any events on the background
                                listening: false,
                            });
                            stageBackground.add(stageBorderBottom);
                            stageBackground.batchDraw();
                        } else {
                            stageBorderBottom.to({
                                duration: .35,
                                fill: borderColor2
                            });
                        }
                    } else {
                        stageBackground.find('.stage_border2').destroy();
                        stageBackground.batchDraw();
                    }
                    resolve();
                    return;
                } else if (stageBorder) {
                    stageBorder.destroy();
                    stageBackground.find('.stage_border2').destroy();
                    stageBackground.batchDraw();
                    resolve();
                    return;
                } else {
                    resolve();
                }
            } else {
                resolve();
            }
        }));
    }

    async updateTextColors(color, removeCustomColor = true) {
        return (new Promise((resolve) => {
            let textConfigurations = store.state.textConfigurations;

            if (removeCustomColor) {
                store.commit('updateField', {
                    path: 'stageConfiguration.customTextColor',
                    value: null
                });
            }

            if (textConfigurations.length > 0) {
                textConfigurations.forEach((textConfiguration, idx) => {
                    textConfigurations[idx].fill = color
                });

                store.commit('updateField', {
                    path: 'textConfigurations',
                    value: textConfigurations
                });

                this.runTextPipeline();
                resolve();
            } else {
                resolve();
            }
        }));
    }

    async generateTempStagePreview() {
        return (new Promise((resolve) => {
            // store current stage as image
            let stage = store.state.stage;
            let topLayer = stage.findOne('#top_layer');
            let previewImage = stage.findOne('#stage_preview');
            if (previewImage) {
                previewImage.destroy();
            }

            let dataURL = stage.toDataURL({
                pixelRatio: 1.0,
                mimeType: 'image/png',
                quality: 1.0
            });

            let stagePreview = new Konva.Image.fromURL(dataURL, (image) => {
                image.setAttrs({
                    x: 0,
                    y: 0,
                    listening: false,
                    id: 'stage_preview',
                    width: stage.width(),
                    height: stage.height()
                });
                topLayer.add(image);
                topLayer.opacity(1);
                topLayer.batchDraw();
                resolve();
            });
        }));
    }

    async removeTempStagePreview() {
        return (new Promise((resolve) => {
            let stage = store.state.stage;
            let topLayer = stage.findOne('#top_layer');

            topLayer.to({
                duration: .35,
                opacity: 0,
                easing: Konva.Easings.EaseInOut,
                onFinish: () => {
                    let previewImage = stage.findOne('#stage_preview');
                    if (previewImage) {
                        previewImage.destroy();
                    }
                    resolve();
                }
            });
        }));
    }

    wavesDraggable(draggable = true) {
        let waves = store.state.waveConfigurations;
        let wavesLayer = store.state.stage.findOne('#waves_layer');

        if (store.state.stage && waves.length > 0) {
            for (let idx = 0; idx < waves.length; idx++) {
                let wave = store.state.stage.findOne(`#wave_image_${idx}`);
                if (wave) {
                    wave.listening(draggable);
                    wave.draggable(draggable);
                }
            }
        }

        wavesLayer.batchDraw();
    }

    enableTextTransformers(transformable = true) {
        let textConfigurations = store.state.textConfigurations;
        let stage = store.state.stage;

        if (textConfigurations.length > 0) {
            let textLayer = stage.findOne(`#text_layer`);
            if (textLayer) {
                if (transformable) {
                    for (let idx = 0; idx < textConfigurations.length; idx++) {
                        let textNode = stage.findOne(`#text_node_${idx}`);

                        makeTextTransformer(
                            textNode,
                            idx,
                            stage,
                            store.state.theme.title.toLowerCase() === 'love',
                            store.state.stageConfiguration.border
                        );

                        textNode.draggable(transformable);
                    }

                } else {
                    let textTransformers = stage.find('.text_transformer');
                    textTransformers.destroy();
                }

                textLayer.batchDraw();
            }
        }
    }

    enableBackgroundTextTransformers(transformable = true) {
        let state = store.state;
        let stage = state.stage;

        if (stage) {
            let bg = store.state.stageConfiguration.background;
            let textBackground = stage.findOne('#text_background');
            let stageBackground = stage.findOne('#stage_background');

            if (stageBackground && textBackground && bg.text_style) {
                let backgroundTextTransformer = stage.findOne('.bg_text_transformer');
                if (backgroundTextTransformer) {
                    backgroundTextTransformer.destroy();
                }

                textBackground.draggable(transformable);

                if (transformable) {
                    makeTextTransformer(
                        textBackground,
                        0,
                        stage,
                        false,
                        state.stageConfiguration.border != 'none',
                        true,
                        state.stageConfiguration.background
                    );

                    textBackground.zIndex(1000);
                    stageBackground.moveToTop();
                    stageBackground.batchDraw();
                    stageBackground.canvas._canvas.style.zIndex = 1000;
                } else {
                    textBackground.zIndex(2);
                    stageBackground.moveToBottom();
                    stageBackground.batchDraw();
                    stageBackground.canvas._canvas.style.zIndex = 0;
                    this.setBackgroundOrder();
                }
            }
        }
    }

    /**
     * Return attributes for
     * @param idx
     * @param config
     * @return {{cropX: number, cropY: number, width: number, x: number, y: number, opacity: number, cropWidth: number, cropHeight: number, height: number}}
     */
    getWaveImageAttributes(idx, config, background = false) {
        // fixed values for all images
        let stage = store.state.stage;
        let numFiles = store.state.soundFiles.length;
        let borderWidth = store.state.stageConfiguration.border === 'border' &&
            store.state.stageConfiguration.background.type !== 'image' ?
                    defaults.stageBorderWidthVisible :
                    defaults.stageBorderWidth;
        let height = Math.ceil((stage.height() - borderWidth * 2) / numFiles);
        let width = stage.width() - (borderWidth * 2);
        let x = borderWidth;
        let y = height * idx + borderWidth;
        let imageCropped = !background && config.cropOptions;

        if (background && store.state.stageConfiguration.border !== 'none') {
            width = width + (borderWidth * 2);
            x = x - borderWidth;
            y = idx == 0 ? y - borderWidth : y;
            height = store.state.waveConfigurations.length == 1 ? height + (borderWidth * 2) : (
                idx == 0 || idx == store.state.waveConfigurations.length - 1 ? height + borderWidth : height
            );
        }

        // individual values depending on the config
        let opacity = this._getWaveOpacity(config);

        // if style is "love", display amplitudes next to each other instead of under another
        if(store.state.theme.title.toLowerCase() === 'love') {
            width = stage.width() / 2 - borderWidth;
            height = stage.height() - borderWidth * 2;
            y = 0 + borderWidth;
            x = (width * idx) + borderWidth;

            if (background && store.state.stageConfiguration.border !== 'none') {
                height = stage.height();
                width = stage.width() / 2;
                y = 0;
                x = width * idx;
            }
        }

        let cropWidth = imageCropped ? config.cropOptions.widthPercent * width : 0;
        let cropX = imageCropped ? config.cropOptions.xPercent * width : 0;

        return {
            y: y,
            initialX: x,
            height: height,
            offsetX: imageCropped ? ((((width - cropWidth) / 2) + cropX) * -1) : 0,
            opacity: opacity,
            initialWidth: width,
            width: width,
            x: x,
            clip: imageCropped ? this._getClipOptions(config) : {}
        };
    }

    getBackgroundImageAttributes(width, height, stage, transform) {
        let imageWidth = width;
        let imageHeight = height;
        const aspectRatio = imageWidth / imageHeight;
        const stageRatio = stage.width() / stage.height();

        // check which side is longer to set max size on stage
        if (aspectRatio >= stageRatio) {
            imageWidth = imageWidth > stage.width() ? stage.width() : imageWidth;
            imageHeight = imageWidth / aspectRatio;
        } else {
            imageHeight = imageHeight > stage.height() ? stage.height() : imageHeight;
            imageWidth = imageHeight * aspectRatio;
        }

        return {
            width: transform ? transform.width : imageWidth,
            height: transform ? transform.height : imageHeight,
            id: 'background_image',
            scale: width / imageWidth,
            rotation: transform ? transform.rotation : 0,
            x: transform ? transform.x: 10,
            y: transform ? transform.y: 10
        };
    }

    /**
     * Add a background (Rectangle) to the layer
     * @param layer
     * @param options
     */
    addLayerBackground(layer, options) {
        var background = new Konva.Rect({
            id: options.id,
            x: options.x || 0,
            y: options.y || 0,
            width: options.width || store.state.stage.width(),
            height: options.height || store.state.stage.height(),
            fill: options.backgroundColor,
            zIndex: -999,

            // remove background from hit graph for better perf
            // because we don't need any events on the background
            listening: false,
        });

        layer.add(background);
    }

    //--------------------------------------------------------------
    // Add text layers
    //--------------------------------------------------------------

    /**
     * Remove all the layers containing text
     */
    addTextLayer() {
        var textLayer = store.state.stage.findOne('#text_layer');

        if (textLayer) {
            textLayer.destroyChildren().batchDraw();
        } else {
            textLayer = new Konva.Layer({id: `text_layer`, zIndex: 999});
            store.state.stage.add(textLayer);
        }
    }

    /**
     * Adds the entered texts to the canvas.
     */
    async addTextsToCanvas() {
        return (new Promise((resolve) => {
            var state = store.state;
            var stage = state.stage;
            var textLayer = stage.findOne('#text_layer');
            state.textConfigurations.forEach((textConfiguration, idx) => {
                var text = new Konva.Text({
                    id: `text_node_${idx}`,
                    ...textConfiguration
                });
                text.x(textConfiguration.dragged ? textConfiguration.x : ((stage.width() / 2) - ((text.width() * text.scaleX()) / 2)));
                text.dragBoundFunc((pos) => {
                    let padding = 5;
                    let maxX = stage.width() - (text.getWidth() * text.scaleX()) - padding;
                    let maxY = stage.height() - (text.getHeight() * text.scaleY()) - padding;

                    return {
                        x: pos.x <= padding ? padding : (pos.x >= maxX ? maxX : pos.x),
                        y: pos.y <= padding ? padding : (pos.y >= maxY ? maxY : pos.y)
                    }
                });
                textLayer.add(text);
                textLayer.batchDraw();
            });
            resolve();
        }));
    }

    /**
     * Creates a new empty text line
     */
    addTextLine() {
        var newLine = JSON.parse(JSON.stringify(defaults.textConfiguration));

        store.state.textConfigurations.push(newLine);
    }

    //--------------------------------------------------------------
    // Add QR-Code
    //--------------------------------------------------------------

    removeQRCodeLayers() {
        var layers = store.state.stage.find('.qrcode');

        layers.forEach((node) => {
            node.destroy();
        });
    }

    async runQRCodePipeline(addToCart = false) {
        return (new Promise((resolve) => {
            // removeQRCode layers
            this.removeQRCodeLayers();
            const QRUrl = 'https://artyourvoice.com/qr/' + store.state.uniqueId;

            if(!store.state.qrcode.use) {
                resolve();
                return;
            }
            let elem = document.getElementById('qr-code');
            if(!elem) {
                resolve();
                return;
            }

            if(!elem.querySelector('canvas')) {
                // generate QR code in element #qr-code  (canvas and img elements will be
                // available in that div)
                let code = new QRCode(elem, {
                    text: QRUrl,
                    width: 96,
                    height: 96
                });

                store.commit('updateField', {
                    path: 'qrcode.instance',
                    value: code
                });
            } else if (addToCart && store.state.qrcode.instance) {
                let code = store.state.qrcode.instance;
                code.clear();
                code.makeCode(QRUrl);
            }

            // add the generated qr code to the stage
            var layer = new Konva.Layer({name: 'qrcode', listening: false});
            var htmlImg = new Image();
            var imgProps = this.getQrCodeImageProperties();

            htmlImg.onload = function() {
                var konvaImg = new Konva.Image({
                    ...imgProps,
                    strokeWidth: 6,
                    drawBorder: true,
                    stroke: '#ffffff'
                });
                konvaImg.image(htmlImg);
                konvaImg.cache();

                // add image to the layer
                layer.add(konvaImg);
                layer.moveToTop(konvaImg);
                layer.batchDraw();
                store.state.stage.add(layer);

                resolve();
            }
            // set source (qrcode-canvas to image dataURL)
            elem = document.getElementById('qr-code');
            let src = elem.querySelector('canvas').toDataURL();
            htmlImg.src = src;
        }));
    }

    getQrCodeImageProperties() {
        let qrSizes = {
            sm: 16,
            md: 24,
            lg: 48
        };

        let stageWidth = store.state.stage.width();
        let stageHeight = store.state.stage.height();
        let qrImgSize = 52;
        let buffer = Math.round(stageWidth * 0.05);

        let x=0;
        let y=0;

        switch(store.state.qrcode.position) {
            case 'top-left':
                x = buffer;
                y = buffer;
                break;
            case 'top-right':
                x = stageWidth - (qrImgSize + buffer);
                y = buffer;
                break;
            case 'bottom-left':
                x = buffer;
                y = stageHeight - (qrImgSize + buffer);
                break;
            case 'bottom-right':
                x = stageWidth - (qrImgSize + buffer);
                y = stageHeight - (qrImgSize + buffer);
                break;
        }

        return {
            x: x,
            y: y,
            width: qrImgSize,
            height: qrImgSize,
            listening: false
        };
    }

    //--------------------------------------------------------------
    // Generating and downloading the final image
    //--------------------------------------------------------------

    /**
     * Generate a small version of the image for preview
     */
    generateFinalImagePreview() {
        if(!store.state.stage || !store.state.stage.toDataURL) {
            return;
        }

        setTimeout(() => {
            const stage = store.state.stage;
            const dataURL = stage.toDataURL({
                pixelRatio: 1.0,
                mimeType: 'image/jpeg',
                quality: 1.0
            });
            const finalPreview = document.getElementById('ayv-thumbnail');

            if(!finalPreview) {
                return;
            }

            this.setResponsivePreviewSize();
            finalPreview.style.setProperty('background-image', 'url("' + dataURL + '")');
        }, 200);
    }

    setResponsivePreviewSize() {
        const finalPreview = document.getElementById('ayv-thumbnail');
        const previewContainer = document.getElementById('preview-container');
        const sofaRealWidth = 218;
        const sofaImageWidth = 770;
        const backgroundZoomFactor = window.outerWidth <= 600 ? 1.35 : 1.08;
        const previewBackgroundRealWidth = 1200;
        const previewBackgroundRealHeight = 1173;
        const portraitRealBottom = window.outerWidth <= 600 ? 300 : 500;
        const sofaRatio = sofaImageWidth/previewBackgroundRealWidth;
        const previewBackgroundRendered = this.getPreviewImageRenderedDimension();
        let selectedFrameSize = store.state.selectedFrameSize;
        const stageConfig = store.state.stageConfiguration;

        if (!selectedFrameSize) {
            if (stageConfig.format == '3-2') {
                selectedFrameSize = {width: 120, height: 80};
            } else if (stageConfig.format == 'panorama') {
                selectedFrameSize = {width: 120, height: 60};
            } else {
                selectedFrameSize = {width: 120, height: 90};
            }
        }

        const sofaPortraitRealRatio = selectedFrameSize.width / sofaRealWidth;
        const sofaPixelWidth = previewBackgroundRendered.width * sofaRatio;
        const renderedBackgroundHeightRatio = previewBackgroundRendered.height / previewBackgroundRealHeight;
        if (finalPreview) {
            let width = sofaPortraitRealRatio * sofaPixelWidth;
            let height = (selectedFrameSize.height / selectedFrameSize.width) * width;

            let bottom = renderedBackgroundHeightRatio * portraitRealBottom - ((previewBackgroundRendered.height - previewContainer.offsetHeight) / 2);
            if (bottom < 10) {
                bottom = 10;
            }

            if (stageConfig.orientation !== 'landscape') {
                // swap width and height
                [width, height] = [height, width];
            }

            const maxHeight = previewContainer.offsetHeight - bottom - 10;
            if (height >= maxHeight) {
                const imageRatio = height / width;
                height = maxHeight;
                width = maxHeight / imageRatio;
            }

            finalPreview.style.setProperty('bottom', bottom + 'px');
            finalPreview.style.setProperty('width', (width * backgroundZoomFactor) + 'px');
            finalPreview.style.setProperty('height', (height * backgroundZoomFactor) + 'px');
        }
    }

    getPreviewImageRenderedDimension() {
        const fullBgWidth = 1200;
	    const fullBgHeight = 1173;
	    const fullBgRatio = fullBgWidth / fullBgHeight;
        const previewBackground = document.querySelector('.preview-wrapper');
        const backgroundContainerWidth = previewBackground.offsetWidth;
        const backgroundContainerHeight = previewBackground.offsetHeight;
        const backgroundContainerRatio = backgroundContainerWidth / backgroundContainerHeight;

        const width = (backgroundContainerRatio > fullBgRatio) ?
            backgroundContainerWidth :
            backgroundContainerHeight * fullBgRatio;

        const height = (backgroundContainerRatio < fullBgRatio) ?
            backgroundContainerHeight :
            backgroundContainerWidth * fullBgRatio;

        return {width: width, height: height};
    }

    showWaveDragGuides() {
        const state = store.state;
        const stage = state.stage;
        let guidePoints = [Math.round(stage.height() / 2)];
        const layer = stage.findOne('#waves_layer');
        const wavesLength = state.waveConfigurations.length;

        if (layer) {
            if (state.theme.title.toLowerCase() === 'family' && wavesLength > 1) {
                const borderWidth = store.state.stageConfiguration.border === 'border' &&
                    store.state.stageConfiguration.background.type !== 'image' ?
                    defaults.stageBorderWidthVisible :
                    defaults.stageBorderWidth;
                const height = stage.height() - (2 * borderWidth);
                const chunk = height / wavesLength;
                const middleLine = chunk / 2 + borderWidth;

                for (let i = 0; i < wavesLength; i++) {
                    guidePoints.push(Math.round(i * chunk + middleLine));
                }
            }

            guidePoints.forEach((y) => {
                var line = new Konva.Line({
                    points: [-6000, 0, 6000, 0],
                    stroke: 'rgb(0, 161, 255)',
                    strokeWidth: 1,
                    name: 'wave-guide',
                    dash: [4, 6],
                    x: 0,
                    y: y,
                });
                layer.add(line);
                layer.batchDraw();
            });
        }
    }

    hideWaveDragGuides() {
        const layer = store.state.stage.findOne('#waves_layer');

        if (layer) {
            layer.find('.wave-guide').destroy();
            layer.batchDraw();
        }
    }

    async clearConfiguratorFormFields() {
        return (new Promise(async (resolve) => {
            // replace image input with file
            const inputsToClear = document.querySelectorAll(`[id^="mfb-printimage"], [id^="mfb-image"], [id^="mfb-videoExtra-"], [id^="mfb-soundfile"]`);

            for (let i = 0; i < inputsToClear.length; i++) {
                inputsToClear[i].value = "";
            }

            resolve();
        }));
    }

    async uploadPortraitConfig() {
        return (new Promise(async (resolve) => {
            const state = store.state;
            const token = await this.getAccessToken();
            const portrait = state.stageConfiguration.orientation === 'portrait';
            const selectedWidth = portrait ? state.selectedFrameSize.height * 100 : state.selectedFrameSize.width * 100;
            const selectedHeight = portrait ? state.selectedFrameSize.width * 100 : state.selectedFrameSize.height * 100;
            const pixelRatio = selectedWidth / state.stage.width();
            const chunkWidth = Math.ceil(6000000 / selectedHeight);
            const chunkNumber = Math.ceil(selectedWidth / chunkWidth);
            const mediaFolderId = await this.getMediaFolderId(token);
            const loaderSteps = chunkNumber + 3

            this.setloaderPercentage((1/loaderSteps));
            await this.generateFinalImage(token, mediaFolderId);

            this.removeQRCodeLayers();

            for (let i = 0; i < chunkNumber; i++) {
                await this.generatePrintImageChunk(i, token, mediaFolderId, chunkNumber, pixelRatio, selectedWidth);
                this.setloaderPercentage(((i+2)/loaderSteps));
            }

            await this.setCustomFieldOptions();
            this.setloaderPercentage(((chunkNumber + 2)/loaderSteps));
            await this.addSoundFilesToCustomFields(token, mediaFolderId);
            this.setloaderPercentage(1);
            resolve();
        }));
    }

    async generatePrintImageChunk(number, token, mediaFolderId, chunkNumber, pixelRatio, selectedWidth) {
        return (new Promise(async (resolve) => {
            const stage = store.state.stage;
            const chunkWidth = Math.ceil(stage.width() / chunkNumber);
            const widthOversize = (Math.floor(chunkWidth * pixelRatio) * chunkNumber) - selectedWidth;
            let currentChunkWidth = chunkWidth;
            let x = (chunkWidth * number);

            if (((chunkNumber - 1) == number && widthOversize != 0)) {
                currentChunkWidth = chunkWidth - (widthOversize / pixelRatio);
                x = stage.width() - currentChunkWidth;
            }

            const printImageSettings = {
                y: 0,
                quality: 1.0,
                pixelRatio: pixelRatio,
                mimeType: 'image/jpeg',
                width: currentChunkWidth,
                height: stage.height(),
            }

            var chunkUrl = stage.toDataURL({
                x: x,
                ...printImageSettings
            });

            const mediaId = await this.createMediaId(token, mediaFolderId);
            const file  = await this.base64toBlob(chunkUrl, 'image/jpeg');
            const url = `/api/_action/media/${mediaId}/upload?extension=jpg`;

            await this.makeApiRequest(token, 'image/jpeg', url, file);

            // replace image input with file
            var input = document.querySelector(`[id^="mfb-printimage${number}"]`);
            input.value = mediaId;

            resolve();
        }));
    }

    async generateFinalImage(token, mediaFolderId) {
        return (new Promise(async (resolve) => {
            let smallImageURL = store.state.stage.toDataURL({ pixelRatio: 1, quality: 0.9, mimeType: 'image/jpeg'});

            // replace image input with file
            const file  = await this.base64toBlob(smallImageURL, 'image/jpeg');
            const mediaId = await this.createMediaId(token, mediaFolderId);
            const url = `/api/_action/media/${mediaId}/upload?extension=jpg`;

            await this.makeApiRequest(token, 'image/jpeg', url, file);

            let input = document.querySelector(`[id^="mfb-image"]`);
            input.value = mediaId;

            resolve();
        }));
    }

    /**
     * Add soundfiles (1-5 + extra) into the input[type=file] fields in the product detail.
     */
     async addSoundFilesToCustomFields(token, mediaFolderId) {
        var promises = [];

        store.state.soundFiles.forEach((soundFile, idx) => {
            if(soundFile && soundFile.url) {
                promises.push(this.setSoundFile(soundFile, idx+1, token, mediaFolderId));
            }
        });

        var extra = store.state.extraSound.soundFile;
        if (extra && extra.url) {
            promises.push(this.setSoundFile(extra, 'Extra', token, mediaFolderId));
        }

        var video = store.state.extraSound.videoFile;
        if (video) {
            promises.push(this.setVideoFile(video, store.state.extraSound.type, token, mediaFolderId));
        }

        await Promise.all(promises);

        return;
    }


    /**
     * Set additional options into the fields in product detail:
     *  - uniqueId
     *  - use qr code
     *  - use extra sound file
     *  - use digital download
     */
    async setCustomFieldOptions() {
        let promises = [];
        const settings = {
            '[id^="mfb-uniqueProductConfigId-"]': { value: store.state.uniqueId, type: 'hidden'},
            '[id^="mfb-material-"]': { value: store.state.selectedMaterial.title, type: 'hidden'},
            '[id^="mfb-product_code-"]': { value: store.state.selectedMaterial.productCode, type: 'hidden'},
            '[id^="mfb-orientation-"]': { value: store.state.stageConfiguration.orientation, type: 'hidden'},
            '[id^="mfb-margin-"]': { value: (store.state.stageConfiguration.border === 'border' &&
                store.state.stageConfiguration.background.type !== 'image') ?
                defaults.stageBorderWidthVisible :
                defaults.stageBorderWidth, type: 'hidden'},
            '[id^="mfb-format-"]': { value: store.state.selectedFrameSize.width + 'x' + store.state.selectedFrameSize.height, type: 'hidden'},
            '[id^="mfb-portrait_password-"]': { value: store.state.qrcode.password, type: 'hidden'},
            '[id^="mfb-portrait_background_colors-"]': { value: this.getBackgroundColors(), type: 'hidden'},
            '[id^="mfb-topic-"]': { value: store.state.theme.title.toLowerCase(), type: 'select'},
            '[id^="mfb-use_qrcode-"]': { value: store.state.qrcode.use ? '1' : '0', type: 'select'},
            '[id^="mfb-qrcode_position-"]': { value: store.state.qrcode.position, type: 'hidden'},
            '[id^="mfb-use_voice-"]': { value: store.state.qrcode.voice ? '1' : '0', type: 'select'},
            '[id^="mfb-use_password-"]': { value: store.state.qrcode.usePassword ? '1' : '0', type: 'select'},
            '[id^="mfb-use_additional_message-"]': { value: store.state.extraSound.use ? '1' : '0', type: 'select'},
            '[id^="mfb-use_digital_download-"]': { value: store.state.digitalDownload.use ? '1' : '0', type: 'select'},
            '[id^="mfb-unconvertedVideo-"]': { value: '0', type: 'select'}
        }

        for (let selector in settings) {
            promises.push(this.setCustomOption(settings, selector));
        }

        return Promise.all(promises);
    }

    async setCustomOption(settings, selector) {
        return (new Promise(async (resolve) => {
            let input = document.querySelector(selector);
            if (input) {
                if (settings[selector].type == 'checkbox') {
                    input.checked = settings[selector].value;
                    resolve();
                    return;
                }

                input.value = settings[selector].value;
            }
            resolve();
        }));
    }

    getBackgroundColors() {
        let backgroundColors = [];
        const waves = store.state.waveConfigurations;

        for (let idx = 0; idx < waves.length; idx++) {
            backgroundColors.push(waves[idx].backgroundColor);
        }

        return backgroundColors.join();
    }

    setloaderPercentage(value) {
        store.commit('updateField', {
            path: 'loaderPercentage',
            value: value.toFixed(2)
        });
    }

    async getAccessToken() {
        return (new Promise(async (resolve) => {
            const { data } = await axios({
                method: "post",
                url: '/api/oauth/token',
                data: {
                    grant_type: "client_credentials",
                    client_id: getConvertedString(window.portraitUser),
                    client_secret: getConvertedString(window.portraitKey)
                },
                'axios-retry': {
                    retries: 5
                }
            });

            resolve(data.access_token);
        }));
    }

    async createMediaId(token, mediaFolderId) {
        return (new Promise(async (resolve) => {
            const url = '/api/media?_response=true';
            const response = await this.makeApiRequest(token, 'application/json', url, {mediaFolderId: mediaFolderId});
            resolve(response.data.id);
        }));
    }

    async getMediaFolderId(token) {
        return (new Promise(async (resolve) => {
            const url = '/api/search/media-folder';
            const data = {
                limit: 1,
                filter: [
                    {
                    type: "equals",
                    field: "name",
                    value: "Portraits"
                    }
                ]
            };
            const response = await this.makeApiRequest(token, 'application/json', url, data);
            resolve(response.data[0].id);
        }));
    }

    async makeApiRequest(token, contentType, url, postData, attempts = 0) {
        return (new Promise(async (resolve) => {
            // try API request
            await axios({
                method: "post",
                url: url,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": `${contentType}`
                },
                data: postData
            }).then((response) => {
                // resolve promise and return response on success
                resolve(response.data);
            })
            .catch(async (reason) => {
                // if API call fails, retry for 3 times
                if (attempts < 2) {
                    // set timeout after second attempt
                    const timeout = attempts > 0 ? 500 : 0;
                    attempts++;

                    setTimeout(() => {
                        resolve(this.makeApiRequest(token, contentType, url, postData, attempts));
                    }, timeout);
                } else {
                    // show alert if all attempts failed
                    alert('Leider gab es bei der Übertragung ein Problem! Bitte überprüfen Sie Ihre Netzwerkverbindung und versuchen es erneut.');
                    // give user the chance to resend portrait
                    store.commit('updateField', {
                        path: 'cartBtnClicked',
                        value: false
                    });
                    // break promise chain to prevent unneccesary uploads
                    throw new Error("Network Error!");
                }
            });
        }));
    }

    async base64toBlob(base64String, type) {
        return (new Promise(async (resolve) => {
            var byteString = atob(base64String.split(',')[1]);
            var arrayBuffer = new ArrayBuffer(byteString.length);
            var unit8Array = new Uint8Array(arrayBuffer);

            for (var i = 0; i < byteString.length; i++) {
                unit8Array[i] = byteString.charCodeAt(i);
            }

            resolve(new Blob([arrayBuffer], { type: type }));
        }));
    }

    async setVideoFile(videoFile, type,  token, mediaFolderId) {
        const contentType = type && type == 'video/quicktime' ? 'video/quicktime' : 'video/mpeg';
        const file = await this.base64toBlob(videoFile, contentType);
        const mediaId = await this.createMediaId(token, mediaFolderId);
        const extension = type && type == 'video/quicktime' ? 'MOV' : 'mp4';
        const url = `/api/_action/media/${mediaId}/upload?extension=${extension}`;

        await this.makeApiRequest(token, contentType, url, file);

        let input = document.querySelector('[id^="mfb-videoExtra-"]');
        input.value = mediaId;
        let unconvertedVideoInput = document.querySelector('[id^="mfb-unconvertedVideo-"]');
        unconvertedVideoInput.value = extension == 'MOV' ? '1' : '0';
    }

    async setSoundFile(soundFile, idx, token, mediaFolderId) {
        const file = await fetch(soundFile.url).then(r => r.blob());
        const mediaId = await this.createMediaId(token, mediaFolderId);
        const url = `/api/_action/media/${mediaId}/upload?extension=mp3`;

        await this.makeApiRequest(token, 'audio/mpeg', url, file);

        let input = document.querySelector(`[id^="mfb-soundfile${idx}"]`);
        input.value = mediaId;
    }
};


export default (new Pipeline());
